import { createContext, useState, FunctionComponent, ReactElement } from 'react';
import { AlgoEngineDestination, AlgoEngineDestinations, DEFAULT_ALGO_ENGINE_DESTINATION, isAlgoEngineDestination } from '../utils/apiRoutes';

export type userTypes = 'manager' | 'trader';

export enum USER {
  MANAGER = 'manager',
  TRADER = 'trader',
}

export type AlgoEngineLoginStatus = 'idle' | 'loading' | 'success' | 'failed' | 'skipped';

export interface IAlgoEngineBootstrapData {
  ClientIds: string[];
  OptSymbols: any;
  FutSymbols: any;
}

export interface IAlgoEngineDestinationState {
  token: string;
  loginStatus: AlgoEngineLoginStatus;
  error: string;
  data: IAlgoEngineBootstrapData;
  lastUpdated: string;
}

export type IAlgoEngineByDestination = Record<AlgoEngineDestination, IAlgoEngineDestinationState>;

export interface IUser {
  accountId: string[];
  userId: string;
  userType: userTypes;
  sessionId: string;
  accountName: string;
  algoEngineToken: string;
  algoEngineData: any;
  selectedDestination?: AlgoEngineDestination;
  algoEngineByDestination?: IAlgoEngineByDestination;
}

interface IProps {
  children: ReactElement;
}

export interface IContextState {
  userData: IUser | null;
  selectedTraderAccount: string | null;
}

const emptyAlgoEngineData: IAlgoEngineBootstrapData = {
  ClientIds: [],
  OptSymbols: {},
  FutSymbols: {},
};

export const createInitialAlgoEngineByDestination = (): IAlgoEngineByDestination => ({
  [AlgoEngineDestinations.NON_COLO]: {
    token: '',
    loginStatus: 'idle',
    error: '',
    data: { ...emptyAlgoEngineData },
    lastUpdated: '',
  },
  [AlgoEngineDestinations.EMKAY_COLO_NSE]: {
    token: '',
    loginStatus: 'idle',
    error: '',
    data: { ...emptyAlgoEngineData },
    lastUpdated: '',
  },
  [AlgoEngineDestinations.EMKAY_COLO_BSE]: {
    token: '',
    loginStatus: 'idle',
    error: '',
    data: { ...emptyAlgoEngineData },
    lastUpdated: '',
  },
});

const hasBootstrapData = (data: any): boolean => {
  if (!data || typeof data !== 'object') return false;
  return Array.isArray(data.ClientIds)
    || Object.keys(data.OptSymbols || {}).length > 0
    || Object.keys(data.FutSymbols || {}).length > 0;
};

export const normalizeAlgoEngineUserData = (rawUserData: any): IUser => {
  const normalizedDestination: AlgoEngineDestination = isAlgoEngineDestination(rawUserData?.selectedDestination)
    ? rawUserData.selectedDestination
    : DEFAULT_ALGO_ENGINE_DESTINATION;

  const initialByDestination = createInitialAlgoEngineByDestination();
  const existingByDestination = rawUserData?.algoEngineByDestination || {};

  const normalizedByDestination: IAlgoEngineByDestination = {
    [AlgoEngineDestinations.NON_COLO]: {
      ...initialByDestination[AlgoEngineDestinations.NON_COLO],
      ...(existingByDestination[AlgoEngineDestinations.NON_COLO] || {}),
      data: {
        ...emptyAlgoEngineData,
        ...(existingByDestination[AlgoEngineDestinations.NON_COLO]?.data || {}),
      },
    },
    [AlgoEngineDestinations.EMKAY_COLO_NSE]: {
      ...initialByDestination[AlgoEngineDestinations.EMKAY_COLO_NSE],
      ...(existingByDestination[AlgoEngineDestinations.EMKAY_COLO_NSE] || {}),
      data: {
        ...emptyAlgoEngineData,
        ...(existingByDestination[AlgoEngineDestinations.EMKAY_COLO_NSE]?.data || {}),
      },
    },
    [AlgoEngineDestinations.EMKAY_COLO_BSE]: {
      ...initialByDestination[AlgoEngineDestinations.EMKAY_COLO_BSE],
      ...(existingByDestination[AlgoEngineDestinations.EMKAY_COLO_BSE] || {}),
      data: {
        ...emptyAlgoEngineData,
        ...(existingByDestination[AlgoEngineDestinations.EMKAY_COLO_BSE]?.data || {}),
      },
    },
  };

  if (!rawUserData?.algoEngineByDestination) {
    normalizedByDestination[AlgoEngineDestinations.NON_COLO] = {
      ...normalizedByDestination[AlgoEngineDestinations.NON_COLO],
      token: rawUserData?.algoEngineToken || '',
      data: hasBootstrapData(rawUserData?.algoEngineData) ? rawUserData.algoEngineData : { ...emptyAlgoEngineData },
      loginStatus: rawUserData?.algoEngineToken ? 'success' : 'idle',
      error: '',
      lastUpdated: rawUserData?.algoEngineToken ? new Date().toISOString() : '',
    };
  }

  const selectedAlgoState = normalizedByDestination[normalizedDestination];

  return {
    ...rawUserData,
    selectedDestination: normalizedDestination,
    algoEngineByDestination: normalizedByDestination,
    algoEngineToken: selectedAlgoState?.token || rawUserData?.algoEngineToken || '',
    algoEngineData: hasBootstrapData(selectedAlgoState?.data) ? selectedAlgoState.data : (rawUserData?.algoEngineData || { ...emptyAlgoEngineData }),
  } as IUser;
};

export const getAlgoEngineStateByDestination = (userData: IUser | null, destination: AlgoEngineDestination): IAlgoEngineDestinationState => {
  const fallback = createInitialAlgoEngineByDestination()[destination];
  if (!userData || !userData.algoEngineByDestination) return fallback;
  return userData.algoEngineByDestination[destination] || fallback;
};

export const getSelectedAlgoEngineState = (userData: IUser | null): IAlgoEngineDestinationState => {
  const selectedDestination = userData?.selectedDestination;
  const destination = isAlgoEngineDestination(selectedDestination)
    ? selectedDestination
    : DEFAULT_ALGO_ENGINE_DESTINATION;
  return getAlgoEngineStateByDestination(userData, destination);
};

const initialState: IContextState = {
  userData: null,
  selectedTraderAccount: null
};

const AppContext = createContext<any>(null);

const AppContextProvider: FunctionComponent<IProps> = ({ children }: IProps) => {
  const [contextState, setContext] = useState(initialState);

  return <AppContext.Provider value={[contextState, setContext]}>{children}</AppContext.Provider>;
};

export { AppContext, AppContextProvider };
